/**
 * Universal Modal Handler
 * Handles email capture from any form/CTA and transitions to universal modal
 * 
 * Usage:
 * 1. Add class "pcm-modal-trigger" to any form or button
 * 2. Optional data attributes:
 *    - data-modal-email-source="selector" - Where to get email from (for forms)
 *    - data-modal-email="email@example.com" - Hardcoded email (for buttons)
 *    - data-modal-form-via="SOURCE" - Form source identifier
 *    - data-modal-target="#modal-id" - Which modal to open (default: #pcm-universal-modal)
 *    - data-modal-email-step="1"/"0" - Force the modal's email step, or force it
 *      to be skipped. Omit it (the normal case) and the modal decides: it asks
 *      for the email unless one has already been captured for this visitor.
 *    - data-modal-redirect="/url/" - Where the modal's form sends the visitor
 *      once it completes. Belongs to the trigger, not the modal, so each CTA on
 *      a page can send the visitor somewhere different.
 *    - data-modal-redirect-target="_blank" - Open that destination in a new tab
 *      instead of navigating the current one (default: navigate current tab).
 *    - data-modal-offer="samples" - Word the email and address steps as
 *      production's free-samples modal did. Omit it for the neutral
 *      "Get Started!" wording production used on every other button.
 *    - data-modal-email-heading / data-modal-email-button - Replace the email
 *      step's heading or button text for this trigger only.
 *    - data-modal-chat="1" - Offer the "Talk to an Expert" step after the
 *      address step, as production's modals did on pages with chat (see
 *      pcm_lead_page_chat() in inc/forms/lead-vias.php). The modal still
 *      skips it outside chat hours.
 *    - data-modal-prefer-popup - Open the page's sitewide popup instead, when
 *      it has one: the promo bar's "claim offer" advertises that popup's
 *      offer (1,000 free cards), which the universal modal's $50 steps don't
 *      match. The universal modal is the fallback on pages without a popup.
 *    - data-modal-extra='[{"name":"comment[Design]","value":"..."}]' - Extra
 *      fields a button sends with the email step (a form trigger sends its
 *      own inputs instead). comment[...] fields land in the lead's comment.
 * 
 * Examples:
 * <form class="pcm-modal-trigger banner-form-wrapper" data-modal-form-via="BANNER">
 * <button class="pcm-modal-trigger" data-modal-email="test@example.com" data-modal-form-via="CTA">
 * <button class="pcm-modal-trigger" data-modal-form-via="CTA" data-modal-redirect="/thank-you/">
 */
(function($) {
  'use strict';
  
  /**
   * data-modal-email-step is a three-way switch, so an absent attribute has to
   * stay distinguishable from an explicit "0": undefined lets the modal decide
   * from whether an email has actually been captured yet, while "1"/"0" are the
   * trigger overriding that decision.
   */
  function readEmailStepPreference($el) {
    const raw = $el.attr('data-modal-email-step');

    if (typeof raw === 'undefined' || raw === '') {
      return undefined;
    }

    return !(raw === '0' || raw === 'false' || raw === 'no');
  }

  /**
   * Fields a trigger form collected besides the email - ref1000's required
   * referral name, say - so they reach the lead with the email step instead
   * of being dropped at the hand-off. The email itself, the honeypots and the
   * form plumbing the modal already carries are left out.
   */
  const HANDOFF_SKIP = ['email', 'email_address', 'contact_me', '_pcm_form_submit_nonce', '_wp_http_referer', 'form_via', 'via', 'industry'];

  function readExtraFields($form) {
    return $form.serializeArray().filter(function(field) {
      return HANDOFF_SKIP.indexOf(field.name) === -1;
    });
  }

  /**
   * Where this trigger wants the modal's form to send the visitor once it
   * completes, if anywhere.
   */
  function readRedirect($el) {
    return {
      redirect: ($el.data('modal-redirect') || '').toString().trim(),
      redirectTarget: $el.data('modal-redirect-target') === '_blank' ? '_blank' : '_self',
      offer: ($el.attr('data-modal-offer') || '').trim(),
      emailHeading: ($el.attr('data-modal-email-heading') || '').trim(),
      emailButton: ($el.attr('data-modal-email-button') || '').trim(),
      chat: $el.attr('data-modal-chat') === '1'
    };
  }

  /**
   * What's wrong with an email, in the same words the lead forms use
   * (PCMForms.emailProblem in pcm-form-handler), or '' when it's usable.
   * The old pattern is the fallback if that script isn't on the page.
   */
  function emailProblemFor(email) {
    if (window.PCMForms && typeof window.PCMForms.emailProblem === 'function') {
      return window.PCMForms.emailProblem(email);
    }

    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? '' : 'Please enter a valid email address.';
  }

  /**
   * Show an email problem on the field itself, as the browser's validation
   * bubble - it fits every layout these boxes sit in. While the visitor
   * edits, the field keeps the current problem (or none), so pressing the
   * button again shows the up-to-date message. A trigger with a preset email
   * and no field falls back to an alert.
   */
  function showEmailProblem($field, message) {
    const field = $field[0];

    if (!field || typeof field.setCustomValidity !== 'function' || typeof field.reportValidity !== 'function') {
      alert(message);
      return;
    }

    field.setCustomValidity(message);
    field.reportValidity();

    $field.off('input.pcmEmailProblem').on('input.pcmEmailProblem', function() {
      this.setCustomValidity(emailProblemFor(this.value));
    });
  }

  $(document).ready(function() {

    // Handle form submissions
    $(document).on('submit', 'form.pcm-modal-trigger', function(e) {
      e.preventDefault();
      
      const $form = $(this);
      const $submitBtn = $form.find('button[type="submit"]');
      const originalBtnText = $submitBtn.data('original-text') || $submitBtn.html();
      
      // Store original button text if not already stored
      if (!$submitBtn.data('original-text')) {
        $submitBtn.data('original-text', originalBtnText);
      }
      
      // Disable button and show submitting state
      $submitBtn.prop('disabled', true).html('Submitting...');
      
      // Get email from form or data attribute
      const emailSource = $form.data('modal-email-source') || 'input[name="email"]';
      const email = ($form.data('modal-email') || $form.find(emailSource).val() || '').toString().trim();
      const formVia = $form.data('modal-form-via') || 'FORM';
      const modalTarget = $form.data('modal-target') || '#pcm-universal-modal';
      
      // Validate email, on the field, saying what to fix
      const emailProblem = email ? emailProblemFor(email) : 'Please enter your email address.';

      if (emailProblem) {
        showEmailProblem($form.find(emailSource).first(), emailProblem);
        $submitBtn.prop('disabled', false).html(originalBtnText);
        return false;
      }
      
      // Open modal
      openModalWithEmail(email, formVia, modalTarget, $.extend({
        emailStep: readEmailStepPreference($form),
        extraFields: readExtraFields($form)
      }, readRedirect($form)), function() {
        // Reset button after modal opens
        setTimeout(function() {
          $submitBtn.prop('disabled', false).html(originalBtnText);
        }, 300);
      }, function() {
        // Reset button on error
        $submitBtn.prop('disabled', false).html(originalBtnText);
      });
      
      return false;
    });
    
    /**
     * Show the page's sitewide popup (pcm-popup-system renders at most one).
     *
     * @return {boolean} Whether there was one to show.
     */
    function openPagePopup() {
      const manager = window.PCMPopupManager;
      const $wrapper = $('.pcm-popup-wrapper').first();

      if (!$wrapper.length || !manager || typeof manager.showPopup !== 'function') {
        return false;
      }

      manager.showPopup($wrapper, $wrapper.data('popup-id'));
      return true;
    }

    // Handle button/link clicks
    $(document).on('click', 'button.pcm-modal-trigger, a.pcm-modal-trigger, .pcm-modal-trigger:not(form)', function(e) {
      e.preventDefault();
      
      const $trigger = $(this);

      if ($trigger.is('[data-modal-prefer-popup]') && openPagePopup()) {
        return false;
      }
      const emailSource = $trigger.data('modal-email-source');
      let email = $trigger.data('modal-email') || '';
      const formVia = $trigger.data('modal-form-via') || 'CTA';
      const modalTarget = $trigger.data('modal-target') || '#pcm-universal-modal';
      const extra = $trigger.data('modal-extra');
      const options = $.extend({
        emailStep: readEmailStepPreference($trigger),
        extraFields: Array.isArray(extra) ? extra : []
      }, readRedirect($trigger));

      if (!email && emailSource) {
        const $context = $trigger.closest('.pcm-modal-context');
        const $emailField = $context.length ? $context.find(emailSource).first() : $(emailSource).first();
        if ($emailField.length) {
          email = $emailField.val() || '';
        }
      }
      
      openModalWithEmail(email, formVia, modalTarget, options);
      
      return false;
    });
    
    /**
     * Open modal with email data
     *
     * @param {string} email       Address collected by this trigger, if any.
     * @param {string} formVia     Attribution tag for the lead.
     * @param {string} modalTarget Selector of the modal to open.
     * @param {Object} options     { emailStep, redirect, redirectTarget,
     *                             extraFields } - see the data attributes
     *                             documented at the top.
     * @param {Function} onSuccess Called once the modal is on screen, which
     *                             can be after a round trip: openPCMModal saves
     *                             an email it's handed before opening.
     */
    function openModalWithEmail(email, formVia, modalTarget, options, onSuccess, onError) {
      const opts = options || {};
      // Wait for openPCMModal to be defined
      const attemptOpen = function(attempt) {
        if (typeof window.openPCMModal === 'function') {
          
          const $modalForm = $(modalTarget).find('form[data-multistep-form]');
          
          if (!$modalForm.length) {
            console.error('[Modal Handler] Modal form not found in', modalTarget);
            if (onError) onError();
            return false;
          }
          
          // Drop only a previously injected duplicate - the modal's own
          // email field (its first step asks for one when we have no email)
          // must survive, and the form plugin reads the FIRST
          // input[name="email"] in the form when resolving the address.
          $modalForm.find('input[type="hidden"][name="email"]').remove();
          
          if (email) {
            const $modalEmailField = $modalForm.find('input[name="email"]').not('[type="hidden"]').first();
            
            if ($modalEmailField.length) {
              $modalEmailField.val(email);
            } else {
              $modalForm.append(
                $('<input>').attr({
                  type: 'hidden',
                  name: 'email',
                  value: email
                })
              );
            }
          }
          
          // Update form_via in modal form
          const $modalFormVia = $modalForm.find('input[name="form_via"]');
          if ($modalFormVia.length) {
            $modalFormVia.val(formVia);
          } else {
            $modalForm.append(
              $('<input>').attr({
                type: 'hidden',
                name: 'form_via',
                value: formVia
              })
            );
          }
          
          // The form plugin reads attribution off data-form-via (or an input
          // named "via"), not the form_via input above, so the trigger's
          // source has to be written there too or every modal lead is
          // attributed to the page-level default instead of the CTA clicked.
          $modalForm.attr('data-form-via', formVia).data('form-via', formVia);

          // Open modal using global function. The redirect is handed to
          // openPCMModal rather than written onto the form here, because the
          // modal is shared by every CTA on the page: it has to be re-applied -
          // or cleared - on each open, and the form plugin's step count depends
          // on it, so only the modal itself can keep the two in sync.
          window.openPCMModal(email, {
            emailStep: opts.emailStep,
            redirect: opts.redirect,
            redirectTarget: opts.redirectTarget,
            offer: opts.offer,
            emailHeading: opts.emailHeading,
            emailButton: opts.emailButton,
            chat: !!opts.chat,
            extraFields: opts.extraFields || [],
            onOpen: onSuccess
          });
        } else {
          if (attempt < 20) {
            setTimeout(function() { attemptOpen(attempt + 1); }, 100);
          } else {
            console.error('[Modal Handler] openPCMModal function not found after 20 attempts');
            if (onError) onError();
          }
        }
      };
      
      attemptOpen(1);
    }
    
  });
  
})(jQuery);
